/**
 * how-we-work.js
 * Fetches content for the "How We Work" landing page from Sanity
 * and hydrates all 4 sections, respecting show/hide toggles.
 *
 * Page:   /how-we-work/
 * Schema: servicesLandingPage (singleton, documentId: 'servicesLandingPage')
 */

import { client, getSanityEditLink } from './sanity-client';
import initScrollAnimations from './animations';
import { initHowWeWorkInteraction } from './how-we-work-interaction';

// ─────────────────────────────────────────────────────────────────────────────
// GROQ query — fetch entire singleton
// ─────────────────────────────────────────────────────────────────────────────
const QUERY = `*[_type == "servicesLandingPage" && _id == "servicesLandingPage"][0]{
  _id,
  showHero, heroHeadline, heroSubtitle,
  showWhatWeDo, whatWeDoIntro, serviceCards, whatWeDoQuote,
  showGetStarted, getStartedIntro, engagementModels, onboardingSteps, getStartedGoal,
  showHowWeDoIt, howWeDoItIntro, processSteps, effectivenessPillars, bannerText, bannerWords
}`;

// ─────────────────────────────────────────────────────────────────────────────
// Helper — apply section visibility
// ─────────────────────────────────────────────────────────────────────────────
function applyVisibility(el, show) {
  if (!el) return;
  if (show === false) {
    el.classList.add('d-none');
  } else {
    el.classList.remove('d-none');
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper — add Sanity edit link to a container
// ─────────────────────────────────────────────────────────────────────────────
function addEditLink(container, docId, fieldPath, label) {
  if (!container || !docId) return;
  if (container.querySelector('.sanity-edit-btn')) return;
  const link = getSanityEditLink(docId, 'servicesLandingPage', fieldPath, label);
  if (link) {
    container.classList.add('position-relative');
    container.insertAdjacentHTML('afterbegin', link);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Renderers — one per section
// ─────────────────────────────────────────────────────────────────────────────

function renderHero(data, docId) {
  const section = document.getElementById('sv-hero');
  applyVisibility(section, data.showHero);
  if (data.showHero === false) return;

  const container = document.getElementById('sv-hero-container');
  addEditLink(container, docId, 'heroHeadline', 'Edit Hero');

  const h1 = section.querySelector('h1');
  const sub = section.querySelector('.sv-hero__sub');
  if (h1 && data.heroHeadline) h1.textContent = data.heroHeadline;
  if (sub && data.heroSubtitle) sub.textContent = data.heroSubtitle;
}

function renderWhatWeDo(data, docId) {
  const section = document.getElementById('sv-what');
  applyVisibility(section, data.showWhatWeDo);
  if (data.showWhatWeDo === false) return;

  const container = document.getElementById('sv-what-container');
  addEditLink(container, docId, 'serviceCards', 'Edit What We Do');

  // Intro text
  const intro = section.querySelector('.sv-what__intro');
  if (intro && data.whatWeDoIntro) intro.textContent = data.whatWeDoIntro;

  // Quote
  const quote = section.querySelector('.sv-what__quote blockquote');
  if (quote && data.whatWeDoQuote) quote.textContent = data.whatWeDoQuote;
}

function renderGetStarted(data, docId) {
  const section = document.getElementById('sv-start');
  applyVisibility(section, data.showGetStarted);
  if (data.showGetStarted === false) return;

  const container = document.getElementById('sv-start-container');
  addEditLink(container, docId, 'engagementModels', 'Edit Get Started');

  // Intro
  const intro = section.querySelector('.sv-start__intro');
  if (intro && data.getStartedIntro) intro.textContent = data.getStartedIntro;

  // Engagement models
  if (data.engagementModels && data.engagementModels.length) {
    const modelEls = section.querySelectorAll('.sv-start__model');
    data.engagementModels.forEach((model, i) => {
      const el = modelEls[i];
      if (!el) return;
      const iconEl = el.querySelector('.model-icon span');
      const h5 = el.querySelector('h5');
      const p = el.querySelector('p');
      if (iconEl && model.icon) {
        iconEl.className = `fe fe-${model.icon}`;
      }
      if (h5 && model.title) h5.textContent = model.title;
      if (p && model.description) p.textContent = model.description;
    });
  }

  // Onboarding steps
  if (data.onboardingSteps && data.onboardingSteps.length) {
    const stepEls = section.querySelectorAll('.sv-start__step');
    data.onboardingSteps.forEach((step, i) => {
      const el = stepEls[i];
      if (!el) return;
      const iconEl = el.querySelector('.step-icon span');
      const numEl = el.querySelector('.step-num');
      const titleEl = el.querySelector('.step-title');
      const descEl = el.querySelector('.step-desc');
      if (iconEl && step.icon) iconEl.className = `fe fe-${step.icon}`;
      if (numEl && step.stepNumber) numEl.textContent = step.stepNumber;
      if (titleEl && step.title) titleEl.textContent = step.title;
      if (descEl && step.description) descEl.textContent = step.description;
    });
  }

  // Goal statement
  const goal = section.querySelector('.sv-start__goal');
  if (goal && data.getStartedGoal) {
    // Keep the icon, update just the text
    const icon = goal.querySelector('.fe');
    goal.textContent = data.getStartedGoal;
    if (icon) goal.prepend(icon);
  }
}

function renderHowWeDoIt(data, docId) {
  const section = document.getElementById('sv-how');
  applyVisibility(section, data.showHowWeDoIt);
  if (data.showHowWeDoIt === false) return;

  const container = document.getElementById('sv-how-container');
  addEditLink(container, docId, 'processSteps', 'Edit How We Do It');

  // Intro
  const intro = section.querySelector('.sv-how__intro');
  if (intro && data.howWeDoItIntro) intro.textContent = data.howWeDoItIntro;

  // Process steps
  if (data.processSteps && data.processSteps.length) {
    const stepEls = section.querySelectorAll('.sv-how__proc-step');
    data.processSteps.forEach((step, i) => {
      const el = stepEls[i];
      if (!el) return;
      const numEl = el.querySelector('.proc-num');
      const h5 = el.querySelector('h5');
      const p = el.querySelector('p');
      if (numEl && step.stepNumber) numEl.textContent = step.stepNumber;
      if (h5 && step.title) h5.textContent = step.title;
      if (p && step.description) p.textContent = step.description;
    });
  }

  // Effectiveness pillars
  if (data.effectivenessPillars && data.effectivenessPillars.length) {
    const pillarEls = section.querySelectorAll('.sv-how__pillar');
    data.effectivenessPillars.forEach((pillar, i) => {
      const el = pillarEls[i];
      if (!el) return;
      const iconEl = el.querySelector('.pillar-icon span');
      const h5 = el.querySelector('h5');
      const p = el.querySelector('p');
      if (iconEl && pillar.icon) iconEl.className = `fe fe-${pillar.icon}`;
      if (h5 && pillar.title) h5.textContent = pillar.title;
      if (p && pillar.description) p.textContent = pillar.description;
    });
  }

  // Banner text — highlight bannerWords
  if (data.bannerText) {
    const bannerP = section.querySelector('.sv-how__banner p');
    if (bannerP) {
      const words = (data.bannerWords || '').split(',').map(w => w.trim()).filter(Boolean);
      let text = data.bannerText;
      words.forEach(word => {
        text = text.replace(new RegExp(`\\b${word}\\b`, 'g'), `<span class="sv-highlight">${word}</span>`);
      });
      const icon = bannerP.parentElement.querySelector('.fe');
      bannerP.innerHTML = text;
      if (icon) bannerP.parentElement.prepend(icon);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', async () => {
  // Only run on the services landing page
  if (!document.getElementById('sv-hero')) return;

  try {
    const data = await client.fetch(QUERY);
    if (!data) return; // No Sanity content yet — static HTML stays

    const docId = data._id;
    renderHero(data, docId);
    renderWhatWeDo(data, docId);
    renderGetStarted(data, docId);
    renderHowWeDoIt(data, docId);
    initHowWeWorkInteraction();

    // Re-initialize animations after content injection
    initScrollAnimations();
  } catch (err) {
    console.warn('[how-we-work] Sanity fetch failed — using static fallback:', err.message);
  }
});
